/**
 * B3/B5 (tier2-approval-requests §3) — заявка на согласование как ОТЛОЖЕННОЕ
 * НАМЕРЕНИЕ, а не запись о факте.
 *
 * До пака Tier 2 над проектом был полем в запросе инициатора: он присылал
 * `approver_user_id` и объект подписи, а сервер исполнял действие немедленно. Здесь
 * инвариант перевёрнут: **Tier 2 — это решение второго, названного человека.**
 * Инициатор создаёт заявку, объект НЕ меняется, рядом висит метка «ждёт
 * согласования», и только решение адресата приводит действие в исполнение.
 */
import { z } from 'zod';
import { IsoDateTime, UuidString } from '../primitives.js';

/**
 * Жизненный цикл заявки.
 *
 * `expired` и `withdrawn` — не украшение: заявка без срока и без права отзыва даёт
 * тот же тупик, что архивация без согласующих, только растянутый во времени.
 */
export const ApprovalRequestStatus = z.enum([
  'waiting',
  /**
   * Захвачена решающим и исполняется прямо сейчас.
   *
   * Отдельное состояние, а не «уже approved»: провал исполнения обязан закрыть
   * заявку `rejected`, а не оставить её зелёной при неисполненном действии. Без
   * этого промежутка два параллельных approve пришлось бы разводить чем-то, кроме
   * условного UPDATE, — а это единственная защита, которая действительно работает.
   */
  'executing',
  'approved',
  'rejected',
  'expired',
  'withdrawn',
  /**
   * B74 (Ф6, §9.4 п. 7) — сбой после ответа 1С: отправка, возможно, дошла; повтора нет,
   * решает человек. Подметальщик переводит сюда и зависшие `executing`.
   */
  'executed_unconfirmed',
]);
export type ApprovalRequestStatus = z.infer<typeof ApprovalRequestStatus>;

/**
 * Типы действий, переводимых на заявку этим паком. Имена совпадают с `task_type`
 * матрицы согласования там, где правило есть, — чтобы круг подписантов и уровень
 * брались оттуда, а не из литерала рядом.
 *
 * `project_uncancel` правила в матрице не имеет намеренно: возврат из отмены
 * симметричен отмене и спрашивает её круг (`project.cancel`), что и делает
 * диалог возврата на фронте.
 */
export const ApprovalRequestTaskType = z.enum([
  'project_cancel',
  'project_uncancel',
  'project_archive',
  'project_restore',
  'project_complete',
  'project_close',
  'project_plan_shift',
]);
export type ApprovalRequestTaskType = z.infer<typeof ApprovalRequestTaskType>;

/**
 * Создание заявки.
 *
 * `task_type` принимается СТРОКОЙ, а не enum-ом: гвард на множественную подпись
 * (§7.2-О7) обязан отвечать явным кодом на `period_close_lock` и `budget_lock`, а не
 * прятать их за «неизвестное значение перечисления». Отказ должен называть причину.
 */
export const ApprovalRequestCreateInput = z
  .object({
    task_type: z.string().min(1).max(64),
    subject_kind: z.literal('project'),
    subject_id: UuidString,
    /** Адресат обязателен: ответственность именная (§5). */
    addressee_user_id: UuidString,
    reason: z.string().min(10).max(2000),
    /** Тело исходного запроса: причина, новые даты — всё, что нужно исполнителю. */
    payload: z.record(z.unknown()).default({}),
  })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ApprovalRequestCreateInput = z.infer<typeof ApprovalRequestCreateInput>;

export const ApprovalRequestReassignInput = z
  .object({ addressee_user_id: UuidString, reason: z.string().max(500).optional() })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ApprovalRequestReassignInput = z.infer<typeof ApprovalRequestReassignInput>;

export const ApprovalRequestWithdrawInput = z
  .object({ reason: z.string().max(500).optional() })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ApprovalRequestWithdrawInput = z.infer<typeof ApprovalRequestWithdrawInput>;

export const ApprovalRequestRow = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    task_type: z.string(),
    tier: z.number().int(),
    subject_kind: z.string(),
    subject_id: z.string(),
    status: ApprovalRequestStatus,
    requested_by_user_id: UuidString.nullable(),
    addressee_user_id: UuidString.nullable(),
    decided_by_user_id: UuidString.nullable(),
    decided_at: IsoDateTime.nullable(),
    decision_reason: z.string().nullable(),
    expires_at: IsoDateTime.nullable(),
    base_version: z.string().nullable(),
    payload: z.record(z.unknown()),
    created_at: IsoDateTime,
  })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ApprovalRequestRow = z.infer<typeof ApprovalRequestRow>;

/**
 * B9 — метка «ждёт согласования» на объекте.
 *
 * **Производная от очереди, а не колонка на проекте.** `pending_action` в таблице
 * проекта завёл бы третью ось состояний рядом с `status` и `archived_at` — ровно то,
 * что разводил пак `project-actions-parity` (§6).
 */
export const PendingApprovalRef = z
  .object({
    id: UuidString,
    task_type: z.string(),
    addressee_user_id: UuidString.nullable(),
    expires_at: IsoDateTime.nullable(),
  })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type PendingApprovalRef = z.infer<typeof PendingApprovalRef>;
